import { FC, useMemo, useState } from "react";
import { Charity } from "types/charity";
import { warm } from "style/color";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useGetGrantedCharitiesQuery } from "hooks/use-services/charity.service";
import CharityCard from "components/charity-card/CharityCard";
import useIntersectionObserver from "hooks/use-services/useIntersectionObserver";
import React from "react";
import { Account } from "types/account";

interface GrantedCharitiesListProps {
  selectedAccount: Account;
  limit?: number;
  viewAllSavedCharities?: boolean;
  handleViewAllSavedCharities: () => void;
  handleOpen: (value: boolean) => void;
  selectCharity: (ein: string) => void;

}

export const GrantedCharitiesList: FC<GrantedCharitiesListProps> = ({
  selectedAccount,
  limit,
  viewAllSavedCharities,
  handleOpen,
  handleViewAllSavedCharities,
  selectCharity

}) => {
  const [offset] = useState(0);
  const {
    data,
    fetchNextPage,
    isSuccess: isGetCharitiesSuccess,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useGetGrantedCharitiesQuery(
    selectedAccount?.fundAccountId
      ? {
        activeFundAccountId: selectedAccount?.fundAccountId,
        limit: limit,
        offset: offset,
      }
      : { limit: limit, offset: offset }
  );
  const charitiesData = useMemo<Charity[]>(
    () => (data ? data?.pages?.flatMap((item) => item.data) : []),
    [data]
  );

  const lastElementRefSaved = useIntersectionObserver({
    isFetching: isFetching,
    isLoading: isLoading,
    fetchNextPage: fetchNextPage,
    hasNextPage: hasNextPage,
  });
  const viewAll = () => {
    handleViewAllSavedCharities();
  };
  const refresh = () => {
    refetch();
  };

  return (
    <>
      <Divider
        sx={{ marginY: 2, backgroundColor: warm[600], borderWidth: "1px" }}
      />
      <Box sx={{ mt: 4, display: "flex", alignItems: "center",justifyContent: {
                xs: "space-between",
                sm: "flex-start",
              } }}>
        <Typography
          className="title"
          variant="subtitle2"
          sx={{ color: "neutral.main", mr: 3, fontSize:{
            xs: "13px",
            sm: "16px"
          } }}
        >
          You previously granted to{" "}
        </Typography>
        <Button
          onClick={viewAll}
          className="link"
          variant="text"
          color="primary"
          size="large"
          sx={{ fontSize: {
            xs: "14px",
            sm: "16px"
          },
          minWidth: {
            xs: "80px",
            sm: "150px"
          } }}
        >
          {
            viewAllSavedCharities ? 'View less' : 'View all'
          }

        </Button>
      </Box>
      <Box sx={{ marginY: 4 }}>
        {isGetCharitiesSuccess && (
          <>
            <Grid sx={{ mt: 2 }} container spacing={2}>
              {charitiesData?.map((charity: any) => (
                <Grid key={charity.ein} item xs={12} md={4}>
                  <CharityCard
                    handleOpen={handleOpen}
                    refresh={refresh}
                    charity={charity}
                    selectCharity={selectCharity}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {viewAllSavedCharities && (
          <Box ref={lastElementRefSaved}>
            {isFetchingNextPage && (
              <Grid item xs={12} sm={10} textAlign={"center"} sx={{ m: 3 }}>
                {" "}
                <CircularProgress color="primary" />
              </Grid>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
